import { localizeMarkup } from "../../site/ui-language.js";
import { useLayoutEffect, useMemo } from "react";
import parse, { attributesToProps, domToReact } from "html-react-parser";
import { ExamFooter, ExamHeader } from "./components/ExamChrome.jsx";
import { AppearanceControls } from "./AppearanceSettings.jsx";

const EXAM_PAGES = new Set(["lesen", "horen", "shreiben", "correction", "mundlich"]);
const LEGACY_RUNTIME_URL = "react-dist/zdeutsch-legacy-runtime.js?v=20261013-writing-ux-1";

function resolveScriptUrl(source) {
  return new URL(source, document.baseURI).toString();
}

function isExternalScript(source) {
  return new URL(source, document.baseURI).origin !== window.location.origin;
}

export function loadClassicScript(source) {
  const resolvedSource = resolveScriptUrl(source);
  const existing = Array.from(document.scripts).find(
    (script) => script.dataset.zdReactScript === resolvedSource
  );
  if (existing) {
    return existing.dataset.loaded === "true"
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener("load", resolve, { once: true });
          existing.addEventListener("error", reject, { once: true });
        });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = resolvedSource;
    script.async = false;
    script.dataset.zdReactScript = resolvedSource;
    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      resolve();
    }, { once: true });
    script.addEventListener("error", () => {
      script.remove();
      reject(new Error(`Could not load ${source}`));
    }, { once: true });
    document.body.append(script);
  });
}

export async function preloadLegacyPage(scripts = []) {
  await Promise.all([loadClassicScript(LEGACY_RUNTIME_URL), ...scripts.map((script) => loadClassicScript(script))]);
}

function showBootstrapError(error) {
  const message = document.createElement("div");
  message.className = "fixed inset-x-4 top-4 z-[999] rounded-2xl border border-rose/30 bg-white p-4 text-sm text-rose shadow-panel";
  message.setAttribute("role", "alert");
  message.textContent = error?.message || "ZDeutsch could not start.";
  document.body.append(message);
}

export default function LegacyPage({ markup, scripts, pageName, bodyClassName = "", onNavigate }) {
  const content = useMemo(() => {
    if (!EXAM_PAGES.has(pageName)) {
      return parse(localizeMarkup(markup));
    }

    const options = {
      replace(node) {
        if (node?.type !== "tag") return undefined;
        if (node.attribs?.id === "settings-panel") {
          return <div {...attributesToProps(node.attribs)}>{domToReact(node.children || [], options)}<AppearanceControls /></div>;
        }
        if (node.name === "header") {
          return (
            <ExamHeader pageName={pageName} onNavigate={onNavigate} {...attributesToProps(node.attribs || {})}>
              {domToReact(node.children || [], options)}
            </ExamHeader>
          );
        }
        if (node.name === "footer") {
          return (
            <ExamFooter pageName={pageName} {...attributesToProps(node.attribs || {})}>
              {domToReact(node.children || [], options)}
            </ExamFooter>
          );
        }
        return undefined;
      }
    };
    return parse(localizeMarkup(markup), options);
  }, [markup, pageName]);

  useLayoutEffect(() => {
    let active = true;
    let unmountController = null;
    const previousBodyClassName = document.body.className;
    if (bodyClassName) document.body.className = bodyClassName;
    if (window.ZDEUTSCH_COURSE_DEMO) document.body.classList.add("zd-course-demo");
    const start = async () => {
      try {
        await preloadLegacyPage(scripts);
      } catch (error) {
        if (!scripts.some((script) => isExternalScript(script))) throw error;
        console.warn(error);
      }
      if (active) {
        const mountController = window.ZDeutschLegacyPages?.[pageName];
        if (typeof mountController !== "function") {
          throw new Error(`The ${pageName} exercise controller is unavailable.`);
        }
        unmountController = mountController();
        document.documentElement.dataset.zdReactReady = "true";
        window.dispatchEvent(new CustomEvent("zdeutsch:react-ready", {
          detail: { page: pageName }
        }));
      }
    };
    start().catch((error) => {
      if (active) showBootstrapError(error);
    });
    return () => {
      active = false;
      if (typeof unmountController === "function") unmountController();
      document.body.className = previousBodyClassName;
    };
  }, [bodyClassName, pageName, scripts]);

  return <>{content}</>;
}
